import { MouseEvent, KeyboardEvent } from "react";
import classNames from "classnames";
import { Stack } from "@altimateai/lego";
import styles from "./styles.module.scss";
import IconClickable from "./IconClickable";
import {
  ArrowLeftDoubleIcon,
  ArrowLeftIcon,
  ArrowRightDoubleIcon,
  ArrowRightIcon,
} from "../assets/icons";

interface ExpansionLevelControlsProps {
  leftExpansion: number;
  rightExpansion: number;
  minRange: [number, number];
  onMaxLeft: () => void;
  onExpandLeft: () => void;
  onCollapseLeft: () => void;
  onCollapseRight: () => void;
  onExpandRight: () => void;
  onMaxRight: () => void;
}

const stop = (fn: () => void) => (e: MouseEvent | KeyboardEvent) => {
  e.stopPropagation();
  fn();
};

/**
 * Parents/Children expansion level steppers used inside the lineage Expand
 * popover. Outward arrows grow the band one level (or jump to max); inward
 * arrows collapse it back one level.
 */
const ExpansionLevelControls = ({
  leftExpansion,
  rightExpansion,
  minRange,
  onMaxLeft,
  onExpandLeft,
  onCollapseLeft,
  onCollapseRight,
  onExpandRight,
  onMaxRight,
}: ExpansionLevelControlsProps) => (
  <Stack direction="row" className="al-w-full" gap="xl" justify="between" align="center">
    <div className={classNames(styles.expand_nav, { [styles.disabled]: minRange[0] === -1 })}>
      <div className={styles.expand_nav_btn}>
        <IconClickable
          className={styles.icon}
          onClick={stop(onMaxLeft)}
          testId="expansion-max-left"
          ariaLabel="Expand parents to max"
        >
          <ArrowLeftDoubleIcon />
        </IconClickable>
        <div className={styles.divider} />
        <IconClickable
          className={styles.icon}
          onClick={stop(onExpandLeft)}
          testId="expansion-expand-left"
          ariaLabel="Expand parents one level"
        >
          <ArrowLeftIcon />
        </IconClickable>
        <div className={styles.divider} />
        <IconClickable
          className={styles.icon}
          onClick={stop(onCollapseLeft)}
          testId="expansion-collapse-left"
          ariaLabel="Collapse parents one level"
        >
          <ArrowRightIcon />
        </IconClickable>
      </div>
      <div className="text-blue al-px-2 al-py-1">{leftExpansion}</div>
    </div>
    <div className={classNames(styles.expand_nav, { [styles.disabled]: minRange[1] === -1 })}>
      <div className="text-blue al-px-2 al-py-1">{rightExpansion}</div>
      <div className={styles.expand_nav_btn}>
        <IconClickable
          className={styles.icon}
          onClick={stop(onCollapseRight)}
          testId="expansion-collapse-right"
          ariaLabel="Collapse children one level"
        >
          <ArrowLeftIcon />
        </IconClickable>
        <div className={styles.divider} />
        <IconClickable
          className={styles.icon}
          onClick={stop(onExpandRight)}
          testId="expansion-expand-right"
          ariaLabel="Expand children one level"
        >
          <ArrowRightIcon />
        </IconClickable>
        <div className={styles.divider} />
        <IconClickable
          className={styles.icon}
          onClick={stop(onMaxRight)}
          testId="expansion-max-right"
          ariaLabel="Expand children to max"
        >
          <ArrowRightDoubleIcon />
        </IconClickable>
      </div>
    </div>
  </Stack>
);

export default ExpansionLevelControls;
